import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Modal,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareText } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  CongregationAssignationRow,
  ListResponse,
  ManagedCongregationRow,
  NotificationsResponse,
} from "../api/types";
import { CountrySelect } from "../components/CountrySelect";
import { DataTable } from "../components/DataTable";

interface CongregationFormValues {
  congregation_code: string;
  denomination: string;
  street_number?: string;
  zip_code?: string;
  city?: string;
  state?: string;
  country?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  phone?: string;
  mobile?: string;
  email?: string;
}

/**
 * Sidebar entry "manage_congregations" → /circumscription/manage.
 *
 * The congregations of the zone coordinator's own circumscription: their
 * address and contact person, and what has been planned for each of them. The
 * old application put the planning below the list as a second grid; here it is
 * a drawer, because the calendar is wide and only ever read.
 *
 * Deactivating is the closest thing to a delete this domain has — a
 * congregation stays in the database with active = false, and the server
 * refuses while roles are still assigned to it.
 */
export function CongregationManagementPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<CongregationFormValues>();

  const [editing, setEditing] = useState<ManagedCongregationRow | "new" | null>(null);
  const [assignationsOf, setAssignationsOf] = useState<ManagedCongregationRow | null>(null);

  const congregations = useQuery({
    queryKey: ["circumscription", "congregations"],
    queryFn: () =>
      apiFetch<ListResponse<ManagedCongregationRow>>("/circumscription/congregations"),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["circumscription", "congregations"] });
  }, [queryClient]);

  const save = useMutation({
    mutationFn: (values: CongregationFormValues) =>
      editing === "new" || editing === null
        ? apiFetch<NotificationsResponse>("/circumscription/congregations", {
            method: "POST",
            body: values,
          })
        : apiFetch<NotificationsResponse>(`/circumscription/congregations/${editing.id}`, {
            method: "PUT",
            body: values,
          }),
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      refresh();
    },
    onError: showError,
  });

  const deactivate = useMutation({
    mutationFn: (id: number) =>
      apiFetch<NotificationsResponse>(`/circumscription/congregations/${id}`, {
        method: "DELETE",
      }),
    onSuccess: (data) => {
      // Answers 200 even when it declined — "roles are still assigned" arrives
      // as a notification, not as a status.
      notify(data.notifications);
      refresh();
    },
    onError: showError,
  });

  const openEditor = useCallback(
    (row: ManagedCongregationRow | "new") => {
      setEditing(row);
      // resetFields before filling. The store no longer drops its values when
      // the dialog closes, so a field this handler does not write would keep
      // the previously edited row's value.
      form.resetFields();
      if (row !== "new") {
        form.setFieldsValue({
          congregation_code: row.congregation_code ?? "",
          denomination: row.denomination ?? "",
          street_number: row.street_number ?? "",
          zip_code: row.zip_code ?? "",
          city: row.city ?? "",
          state: row.state ?? "",
          country: row.country ?? "",
          contact_first_name: row.contact_first_name ?? "",
          contact_last_name: row.contact_last_name ?? "",
          phone: row.phone ?? "",
          mobile: row.mobile ?? "",
          email: row.email ?? "",
        });
      }
    },
    [form],
  );

  const askAndDeactivate = useCallback(
    (row: ManagedCongregationRow) => {
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: (
          <>
            <Typography.Paragraph>
              {[row.congregation_code, row.denomination].filter(Boolean).join(" · ")}
            </Typography.Paragraph>
            {t("circumscription.confirm_cancel_congregation")}
          </>
        ),
        okText: t("dialog.answer_yes"),
        okButtonProps: { danger: true },
        cancelText: t("dialog.answer_no"),
        onOk: () => deactivate.mutate(row.id),
      });
    },
    [modal, t, deactivate],
  );

  const busy = save.isPending || deactivate.isPending;

  const columns = useMemo<TableProps<ManagedCongregationRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.congregation.congregation_code"),
        dataIndex: "congregation_code",
        width: 90,
        sorter: (a, b) => compareText(a.congregation_code, b.congregation_code),
      },
      {
        title: t("activerecord.attributes.congregation.denomination"),
        dataIndex: "denomination",
        width: 200,
        defaultSortOrder: "ascend",
        sorter: (a, b) => compareText(a.denomination, b.denomination),
      },
      {
        title: t("activerecord.attributes.congregation.street_number"),
        dataIndex: "street_number",
        width: 200,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.congregation.zip_code"),
        dataIndex: "zip_code",
        width: 90,
      },
      {
        title: t("activerecord.attributes.congregation.city"),
        dataIndex: "city",
        width: 150,
        sorter: (a, b) => compareText(a.city, b.city),
      },
      {
        title: t("activerecord.attributes.congregation.state"),
        dataIndex: "state",
        width: 100,
      },
      {
        title: t("activerecord.attributes.congregation.country"),
        dataIndex: "country",
        width: 80,
      },
      {
        title: t("activerecord.attributes.congregation.contact_first_name"),
        dataIndex: "contact_first_name",
        width: 140,
      },
      {
        title: t("activerecord.attributes.congregation.contact_last_name"),
        dataIndex: "contact_last_name",
        width: 140,
      },
      {
        title: t("activerecord.attributes.congregation.phone"),
        dataIndex: "phone",
        width: 140,
      },
      {
        title: t("activerecord.attributes.congregation.mobile"),
        dataIndex: "mobile",
        width: 140,
      },
      {
        title: t("activerecord.attributes.congregation.email"),
        dataIndex: "email",
        width: 200,
        ellipsis: true,
      },
      {
        key: "actions",
        fixed: "right",
        width: 100,
        render: (_value, row) => (
          <Space size={4}>
            <Tooltip title={t("form.update")}>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={busy}
                onClick={(event) => {
                  // The row itself opens the planning.
                  event.stopPropagation();
                  openEditor(row);
                }}
              />
            </Tooltip>
            <Tooltip title={t("congregation.disable")}>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  askAndDeactivate(row);
                }}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [t, busy, openEditor, askAndDeactivate],
  );

  return (
    <Card
      title={t("navigation.manage_congregations")}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor("new")}>
          {t("congregation.create")}
        </Button>
      }
    >
      <DataTable<ManagedCongregationRow>
        columns={columns}
        rows={congregations.data?.data}
        exportAs={t("navigation.manage_congregations")}
        loading={congregations.isPending || busy}
        error={congregations.error}
        onRowClick={setAssignationsOf}
      />

      <Modal
        open={editing !== null}
        title={editing === "new" ? t("congregation.create") : editing?.denomination}
        onCancel={() => setEditing(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: save.isPending }}
        onOk={() => form.submit()}
        width={620}
        destroyOnHidden
      >
        <Form<CongregationFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate(values)}
        >
          <Space align="start" size="middle">
            <Form.Item
              name="congregation_code"
              label={t("activerecord.attributes.congregation.congregation_code")}
              // The server applies these two only when they are present, so an
              // emptied field would silently keep its old value.
              rules={[{ required: true }]}
            >
              <Input style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              name="denomination"
              label={t("activerecord.attributes.congregation.denomination")}
              rules={[{ required: true }]}
            >
              <Input style={{ width: 380 }} />
            </Form.Item>
          </Space>

          <Typography.Title level={5}>{t("congregation.headers.address")}</Typography.Title>

          <Form.Item
            name="street_number"
            label={t("activerecord.attributes.congregation.street_number")}
          >
            <Input />
          </Form.Item>

          <Space align="start" size="middle">
            <Form.Item name="zip_code" label={t("activerecord.attributes.congregation.zip_code")}>
              <Input style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="city" label={t("activerecord.attributes.congregation.city")}>
              <Input style={{ width: 230 }} />
            </Form.Item>
            <Form.Item name="state" label={t("activerecord.attributes.congregation.state")}>
              <Input style={{ width: 120 }} />
            </Form.Item>
          </Space>

          <Form.Item name="country" label={t("activerecord.attributes.congregation.country")}>
            <CountrySelect style={{ width: 240 }} />
          </Form.Item>

          <Typography.Title level={5}>{t("congregation.headers.contact_person")}</Typography.Title>

          <Space align="start" size="middle">
            <Form.Item
              name="contact_first_name"
              label={t("activerecord.attributes.congregation.contact_first_name")}
            >
              <Input style={{ width: 250 }} />
            </Form.Item>
            <Form.Item
              name="contact_last_name"
              label={t("activerecord.attributes.congregation.contact_last_name")}
            >
              <Input style={{ width: 250 }} />
            </Form.Item>
          </Space>

          <Space align="start" size="middle">
            <Form.Item name="phone" label={t("activerecord.attributes.congregation.phone")}>
              <Input style={{ width: 250 }} />
            </Form.Item>
            <Form.Item name="mobile" label={t("activerecord.attributes.congregation.mobile")}>
              <Input style={{ width: 250 }} />
            </Form.Item>
          </Space>

          <Form.Item
            name="email"
            label={t("activerecord.attributes.congregation.email")}
            rules={[{ type: "email" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {assignationsOf && (
        <AssignationsDrawer
          congregation={assignationsOf}
          onClose={() => setAssignationsOf(null)}
        />
      )}
    </Card>
  );
}

/**
 * What a congregation has planned, read-only. The endpoint answers with every
 * week of the circumscription's window — `rewind_weeks` back, `forward_weeks`
 * ahead — so the empty weeks are part of the answer and show up as gaps.
 */
function AssignationsDrawer({
  congregation,
  onClose,
}: {
  congregation: ManagedCongregationRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const assignations = useQuery({
    queryKey: ["circumscription", "congregations", congregation.id, "assignations"],
    queryFn: () =>
      apiFetch<ListResponse<CongregationAssignationRow>>(
        `/circumscription/congregations/${congregation.id}/assignations`,
      ),
  });

  const columns = useMemo<TableProps<CongregationAssignationRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 190,
      },
      {
        title: t("activerecord.attributes.local_assignation.circumscription_event"),
        dataIndex: "circumscription_event",
        width: 100,
        align: "center",
        render: (value: boolean) => (value ? <Tag color="blue">{t("yes")}</Tag> : null),
      },
      {
        title: t("activerecord.attributes.local_assignation.event_type"),
        dataIndex: "event_type_label",
        width: 170,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.local_assignation.additional_notes"),
        dataIndex: "additional_notes",
        width: 180,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.local_assignation.speaker_id"),
        dataIndex: "speaker",
        width: 180,
        ellipsis: true,
      },
      {
        title: t("activerecord.models.circumscription"),
        dataIndex: "speaker_circumscription",
        width: 90,
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "speaker_congregation",
        width: 170,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "speech_schema",
        width: 90,
        align: "right",
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "speech_title",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.language_id"),
        dataIndex: "language_code",
        width: 90,
      },
    ],
    [t],
  );

  return (
    <Drawer
      open
      onClose={onClose}
      size={1100}
      destroyOnHidden
      title={[congregation.congregation_code, congregation.denomination]
        .filter(Boolean)
        .join(" · ")}
    >
      <DataTable<CongregationAssignationRow>
        columns={columns}
        rows={assignations.data?.data}
        exportAs={[congregation.congregation_code, congregation.denomination]
          .filter(Boolean)
          .join(" · ")}
        loading={assignations.isPending}
        error={assignations.error}
        rowKey="week_begin"
      />
    </Drawer>
  );
}
